import { createContext, useCallback, useContext, useMemo } from "react"
import { ReactComponent as UtilizationIcon } from "../../../../assets/icons/map/filter_utilization.svg"
import { FilterIds, useFilterQueryParams } from "../../hooks/filter_query_params"
import { useMapViewData } from "../../hooks/map_view_data"
import { isEmptyObject } from "@ciq/shared/utils/object"
import { isEmptyArray } from "@ciq/shared/utils/array"
import eventBus from "@ciq/shared/hooks/eventBus"

const OverlayDataContext = createContext()

export function OverlayDataProvider({ children }) {
  const data = OverlayData()
  return <OverlayDataContext.Provider value={data}>{children}</OverlayDataContext.Provider>
}

export function useOverlayData() {
  const context = useContext(OverlayDataContext)
  if (!context) {
    throw new Error("useOverlayData must be used within an OverlayDataProvider")
  }
  return context
}

const OverlayData = () => {
  const { semesterSummary, hourlySummary } = useMapViewData()
  const { mapDisplayType, onMapDisplayTypeChange } = useFilterQueryParams()

  const { column: semesterColumn, colorRanges: semesterColorRanges } = semesterSummary
  const { column: hourColumn, colorRanges: hourColorRanges } = hourlySummary

  const filters = useMemo(() => {
    const handleIconClick = key => () => onMapDisplayTypeChange(key)

    const build = (id, label, icon) => ({
      id,
      data: {
        selected: mapDisplayType === id,
        label,
        icon,
        onClick: handleIconClick(id),
      },
    })

    return [
      build(FilterIds.Options.SEMESTER, "Semester", <UtilizationIcon />),
      build(FilterIds.Options.HOURLY, "Hourly", <UtilizationIcon />),
    ]
  }, [mapDisplayType, onMapDisplayTypeChange])

  const hourLegendData = useMemo(() => {
    return buildLegendData(hourColumn, hourColorRanges)
  }, [hourColumn, hourColorRanges])

  const semesterLegendData = useMemo(() => {
    return buildLegendData(semesterColumn, semesterColorRanges)
  }, [semesterColumn, semesterColorRanges])

  return {
    setMapPaddingOffset: useCallback(padding => {
      eventBus.emit("map-padding-changed", padding)
    }, []),
    filters,
    legendData: mapDisplayType === FilterIds.Options.SEMESTER ? semesterLegendData : hourLegendData,
  }
}

/**
 * Build legend data from column and pre-built color ranges.
 *
 * @param {Object} column - The column configuration with label property
 * @param {Array} colorRanges - Pre-built UtilizationRange array from map_view_data
 * @returns {Object} Legend data with name, hasData flag, and colors array
 */
export const buildLegendData = (column, colorRanges) => {
  if (isEmptyObject(column) || isEmptyArray(colorRanges)) {
    return {
      name: column?.label ?? "",
      hasData: false,
      colors: [],
    }
  }

  return {
    name: column.label,
    hasData: true,
    colors: colorRanges,
  }
}
